import { jobActions, contactActions, companyActions } from '../actionTypes';
import {
  _updateRecord,
  _removeRecord,
  _createStore,
  _addRecords,
  _clone,
  _get,
  _filterDataToMap,
} from '../utils';
import createLogger from '../../utils/createLogger';

const DEBUG = true;
const debug = createLogger(DEBUG, `jobsReducer.js`);

function _createCompanyIndexes() {
  let starts = "abcdefghijklmnopqrstuvwxyz0123456789_", companies = {};
  for(let i = 0; i < starts.length; ++i) {
    companies[starts[i]] = [];
  }
  return companies;
}

const initialState = {
  filters: null,
  postings: _createStore(),
  leads: _createStore(),
  companies: [],
  companiesById: {},
  company: {},
  companyRecents: [],
  companiesByLetter: _createCompanyIndexes()

};

/*
 * These are used by the cache management stuff in each reducer
 */
function insertRecord(list, record, byId) {
  debug.enter(`insertRecord`);
  debug.log(`  record:`, record);
  debug.log(`  list:`, list);

  const updateIndex = list.findIndex((listRecord) => record._id === listRecord._id);

  if (updateIndex !== -1) {
    debug.log(`  found matching id`);
    debug.log(`    record:`, record);
    debug.log(`    listRecord:`, list[updateIndex]);
    let newRecord = {...record};
    list[updateIndex] = {
      ...list[updateIndex],
      ...newRecord
    };
    if(byId) byId[newRecord._id] = list[updateIndex];

    return true;
  } else {
    list.push(record);
    if(byId) byId[record._id] = record;
    return true;
  }
}

function mergeLists(oldList, newList, byId) {
  debug.enter(`mergeLists`);
  debug.log(`  oldList:`, oldList);
  debug.log(`  newList:`, newList);

  if (!oldList || oldList.length < 1) {
    debug.log(`  oldList is empty; returning newList`);
    let retval = [...newList];
    if (byId) {
      retval.forEach(item => {
        byId[item._id] = item;
      })
    }
    debug.log(newList);
    return [...newList];
  }

  let mergedList = [...oldList];

  newList.forEach((newRecord) => {
    if (!insertRecord(mergedList, newRecord, byId)) {
      debug.log(`  no matching id; appending`);
      debug.log(`    newRecord:`, newRecord);
      mergedList.push(newRecord);
      if(byId) byId[newRecord._id] = newRecord;
    }
  });
  return mergedList;
}

const filtersReducer = (state = initialState.filters, action) => {
  let filters = state ? _clone(state) : null;
  let category, category_id,
      option, option_id,
      query;

  switch (action.type) {

    case jobActions.GET_JOB_FILTERS_SUCCESS:
      let filters_data = action.data;
      debug.enter(`jobs/filters/GET_FILTERS_SUCCESS`);

      return filters || _filterDataToMap(filters_data);

    case jobActions.GET_JOB_FILTERS_FAILURE:
      return null;


    case jobActions.TOGGLE_FILTER_OPTION:
      category_id = action.category_id;
      option_id = action.option_id;

      debug.enter(`jobs/filters/TOGGLE_FILTER_OPTION`);
      debug.log(`  category_id:`, category_id);
      debug.log(`  option_id:`, option_id);
      debug.log(`  filters:`, filters);

      if (!filters)
        throw Error(`Filters are not initialized; cannot update`);

      category = _get(filters, [category_id], DEBUG);
      option = _get(category, [`options`, option_id], DEBUG);

      if (!!category && !!option) {
        option.active = !option.active;

        if (category.canQuery)
          category.query = ``;

        category.options[option_id] = option;
        filters[category_id] = category;

        debug.log(`  updated filters:`, filters);
        return filters;
      }

      throw Error(`could not find filter: ${category_id}/${option_id}`);


    case jobActions.UPDATE_FILTER_QUERY:
      category_id = action.category_id;
      query = action.query;

      debug.enter(`Reducing UPDATE_FILTER_QUERY`);
      debug.log(`  filters:`, filters);
      debug.log(`  category: ${category_id}`);
      debug.log(`  query: ${query}`);

      if (!filters)
        throw Error(`Filters are not initialized; cannot update`);

      category = _get(filters, [category_id], DEBUG);

      if (category && category.canQuery) {
        category.query = query;

        for (option_id in category.options)
          if (category.options.hasOwnProperty(option_id))
            category.options[option_id].active = false;

        filters[category_id] = category;

        debug.log(`  updated filters:`, filters);
        return filters;
      }

      throw !!category
        ? Error(`Filter category not queryable ${category_id}`)
        : Error(`Filter category not found: ${category_id}`);

    default:
      return filters;
  }
};

export default (state = initialState, action) => {

  if (action.type.match(/FILTER/))
    return { ...state, filters: filtersReducer(state.filters, action) };


  let postings = _clone(state.postings);
  let leads = _clone(state.leads);
  let companies = _clone(state.companies);
  let company = _clone(state.company);
  let companyRecents = _clone(state.companyRecents);
  let id;

  switch (action.type) {

    case jobActions.GET_JOB_POSTINGS_PAGE_SUCCESS:
      if (action.data) {
        debug.enter(`jobs/GET_JOB_POSTINGS_PAGE_SUCCESS`);
        debug.log(`  postings:`, postings);
        postings = _addRecords(action.data, postings);
        debug.log(`  new postings:`, postings);
      } break;

    case jobActions.FLUSH_POSTINGS_CACHE:
      postings = _createStore(); break;

    case jobActions.GET_JOB_LEADS_PAGE_SUCCESS:
      debug.enter(`jobs/GET_JOB_LEADS_PAGE_SUCCESS`);
      if (action.data) {
        leads = _addRecords(action.data, leads)
      } break;

    case jobActions.FLUSH_LEADS_CACHE:
      leads = _createStore(); break;

    case jobActions.GET_JOB_SUCCESS:
      if (action.data) {
        id = action.data._id;
        switch (action.data.status) {
          case `found`:
            _updateRecord(action.data, leads);
            if (postings[id])
              _removeRecord(id, postings);
            break;

          case `closed`:
            if (postings[id])
              _removeRecord(id, postings);
            if (leads[action.data.jobid])
              _removeRecord(id, leads);
            break;

          default:
            _updateRecord(action.data, postings);
            break;
        }
      } break;

    case jobActions.PUT_DUPLICATE_JOB_SUCCESS:
      debug.enter(action.type);
      if (action.data) {
        id = action.data._id;
        debug.log(`job id: ${id}`);
        let status = action.data.status;
        debug.log(`job status: ${status}`);
        if (postings.records[id]) {
          debug.log(`found ${id} in postings:`, {...postings.records});
          _removeRecord(id, postings);
          debug.log(`removed ${id} from postings:`, {...postings.records});
        }
      } break;

    case jobActions.PUT_JOB_SUCCESS:
      debug.enter(action.type);
      if (action.data) {
        id = action.data._id;
        let status = action.data.status;
        debug.log(`job id: ${id}`);
        debug.log(`job status: ${status}`);
        switch (status) {
          case `found`:
            _updateRecord(action.data, leads);
            if (postings.records[id]) {
              debug.log(`found ${id} in postings:`, {...postings.records});
              _removeRecord(id, postings);
              debug.log(`removed ${id} from postings:`, {...postings.records});
            }
            break;

          case `closed`:
            if (postings.records[id]) {
              debug.log(`found ${id} in postings:`, {...postings.records});
              _removeRecord(id, postings);
              debug.log(`removed ${id} from postings:`, {...postings.records});
            }
            if (leads.records[id])
              _removeRecord(id, leads);
            break;

          case `failed`:
            if (postings.records[id]) {
              debug.log(`found ${id} in postings:`, {...postings.records});
              _removeRecord(id, postings);
              debug.log(`removed ${id} from postings:`, {...postings.records});
            }
            if (leads.records[id])
              _removeRecord(id, leads);
            break;

          default:
            debug.log(`updating ${id} in postings:`, {...postings.records[id]});
            debug.log(`update:`, action.data);
            _updateRecord(action.data, postings);
            debug.log(`updated:`, {...postings.records[id]});
            break;
        }
      } break;

    case jobActions.POST_JOB_SUCCESS:
      if (action.data) {
        console.log(action.data);
        let id = action.data._id;
        let status = action.data.status;
        debug.log(`job id: ${id}`);
        debug.log(`job status: ${status}`);
        switch (status) {
          case `found`:
            debug.log(`found ${id} in postings:`, {...postings.records});
            _removeRecord(id, postings);
            debug.log(`removed ${id} from postings:`, {...postings.records});
          case `closed`:
            debug.log(`found ${id} in postings:`, {...postings.records});
            _removeRecord(id, postings);
            debug.log(`removed ${id} from postings:`, {...postings.records});
          default:
            break;
        }
      } break;

    case companyActions.GET_COMPANIES_SUCCESS:
      if (action.data) {
        debug.enter('jobs/GET_COMPANIES_SUCCESS');
        debug.log('retrieved company list');

        let companies = [...state.companies],
          companiesById = {...state.companiesById};
        debug.log(action.data);
        // Add a lowercase name for quicker searches
        let cdata = action.data.map(c => ({
          ...c, lname: c.companyname.toLowerCase(),
          words: c.companyname.toLowerCase().split(' ')
        }));
        companies = mergeLists(companies, cdata, companiesById);
        let companiesByLetter = {...state.companiesByLetter};
        companies.forEach(company => {
          company.words.forEach(word => {
            let letter = word[0];
            if(companiesByLetter.hasOwnProperty(letter)) {
              if(!companiesByLetter[letter].includes(company)) companiesByLetter[letter].push(company);
            } else {
              if(!companiesByLetter["_"].includes(company)) companiesByLetter["_"].push(company);
            }
          })
        });
        return {...state, companies, companiesById, companiesByLetter};
      } break;

    case companyActions.GET_COMPANY_SUCCESS:
      if (action.data) {
        debug.enter(`jobs/GET_COMPANY_SUCCESS`);
        debug.log(`received company metadata:`, action.data);
        let companiesById = {...state.companiesById};
        companiesById[action.data.companyid] = action.data;
        company = action.data;
        return {...state, company, companiesById}
      } break;

    case jobActions.GET_COMPANY_RECENTS_SUCCESS:
      if (action.data) {
        debug.enter(`jobs/GET_COMPANY_RECENTS_SUCCESS`);
        debug.log(`received company recent leads:`, action.data);
        companyRecents = action.data;
        return {...state, companyRecents}
      } break;

    case companyActions.PUT_COMPANY_SUCCESS:
      if (action.data) {
        debug.enter(`jobs/PUT_COMPANY_SUCCESS`);
        debug.log(`received company metadata:`, action.data);
        const company = action.data;
        let companies = [...state.companies],
          companiesById = {...state.companiesById},
          companiesByLetter = {...state.companiesByLetter};
        if (companiesById[company.companyid].companyname !== company.companyname) {
          let newCompanyByLetter = {
            companyid: company.companyid,
            companyname: company.companyname,
            lname: company.companyname.toLowerCase (),
            words: company.companyname.toLowerCase ().split (' '),
          };
          for (let letter in companiesByLetter) {
            if (!companiesByLetter.hasOwnProperty(letter)) continue;
            debug.log('Letter of companybyletter: ', letter);
            let idx = companiesByLetter[letter].findIndex((cbl) => cbl.companyid === company.companyid);
            if (idx !== -1) {
              companiesByLetter[letter].splice(idx, 1);
            }
          }
          for (let word of newCompanyByLetter.words) {
            let start = word[0];
            if (companiesByLetter.hasOwnProperty(start)) {
              companiesByLetter[start].push (newCompanyByLetter);
            }
          }
        }
        companies = mergeLists(companies, [company]);
        companiesById[action.data.companyid] = company;
        return {...state, companies, company, companiesById, companiesByLetter}
      } break;

    case companyActions.POST_COMPANY_SUCCESS:
      if (action.data.company) {
        debug.enter(`jobs/POST_COMPANY_SUCCESS`);
        debug.log(`received successfully posted company`, action.data.company);

        const company = action.data.company;

        let companies = [...state.companies],
          companiesById = {...state.companiesById},
          companiesByLetter = state.companiesByLetter;

        let newCompanyByLetter = {
          companyid: company._id,
          companyname: company.name,
          lname: company.name.toLowerCase(),
          words: company.name.toLowerCase().split(' ')};

        newCompanyByLetter.words.forEach(word => {
            let letter = word[0];
            if(companiesByLetter.hasOwnProperty(letter)) {
              if(!companiesByLetter[letter].includes(newCompanyByLetter))
                  companiesByLetter[letter].push(newCompanyByLetter);
             } else {
               if(!companiesByLetter["_"].includes(newCompanyByLetter))
                 companiesByLetter["_"].push(newCompanyByLetter);
            }
          });
        companies = mergeLists(companies, [company]);
        companiesById[company._id] = company;

        return {...state, companies, company, companiesById, companiesByLetter}
      } break;

    case contactActions.PUT_CONTACTS_SUCCESS:
      if (action.data) {
        debug.enter(`jobs/PUT_CONTACTS_SUCCESS`);
        debug.log(`made changes to sql records and returned updated contact list`);
        company.contacts = action.data;
        return {...state, company}
      } break;
  }

  return {
    ...state,
    postings,
    leads,
    companies,
    companyRecents,
  }
};



// WEBPACK FOOTER //
// ./src/data/jobs/jobsReducer.js